// Fonte única do controle de acesso no front. A régua de papéis (ROLE_RANK +
// ROUTE_MIN_ROLE) saiu: quem decide é a permissão vinda do back, e o mapa
// rota→tela vem do catálogo servido por GET /v1/access/catalog — um lugar só
// para as duas pontas.
//
// Consumido pela guarda de rota (router.tsx) e pela navegação (app-sidebar /
// mobile-nav), então as duas nunca divergem.
import type { PermissionCatalog } from "@/api";

// Rotas que todo usuário logado enxerga. Só são consultadas quando o catálogo
// não carregou — em operação normal quem responde é a permissão.
const BASE_PATHS = ["/", "/dashboard", "/dialer", "/assistant", "/help", "/jornada", "/about"];

// Prefixo mais específico vence, e o casamento é por SEGMENTO: /voicemail não
// casa com /voice.
export function screenKeyForPath(catalog: PermissionCatalog, path: string): string | null {
  let best: string | null = null;
  let bestLen = -1;
  for (const [modKey, mod] of Object.entries(catalog)) {
    for (const [scrKey, scr] of Object.entries(mod.screens)) {
      const casa = path === scr.route || path.startsWith(scr.route + "/");
      if (casa && scr.route.length > bestLen) {
        bestLen = scr.route.length;
        best = `${modKey}.${scrKey}.view`;
      }
    }
  }
  return best;
}

export function canAccess(args: {
  catalog: PermissionCatalog | null;
  permissions: ReadonlySet<string>;
  path: string;
  crossBrand?: boolean;
}): boolean {
  // Consolidado cross-marca não é permissão de marca: é o par (flag de
  // plataforma, cliente do tipo 'grupo') que o back já resolve em `crossBrand`.
  // O tipo do cliente entra na conta porque o cliente 'unica' tem uma marca só:
  // para ele "todas as marcas" seria o relatório da própria marca com outro
  // nome — tela sem função, não tela restrita.
  if (args.path === "/reports/all-brands") return args.crossBrand === true;
  // Catálogo não carregado (o /access/catalog falhou junto com o /access/me):
  // sem ele não dá para saber qual rota é base, então a lista abaixo é a
  // definição. Devolver `true` aqui liberaria /management/users a qualquer um —
  // o back negaria, mas a tela abriria vazia e pareceria bug.
  if (!args.catalog)
    return BASE_PATHS.some((b) => args.path === b || args.path.startsWith(b + "/"));
  const key = screenKeyForPath(args.catalog, args.path);
  // Rota fora do catálogo = base. Travar aqui trocaria um furo por um loop de
  // redirect na guarda; quem impede o descuido é o teste de cobertura.
  if (!key) return true;
  return args.permissions.has(key);
}

// Escopo de dado e botões: continuam derivando do papel-base da role, não das
// permissões — na fase 1 o back também decide assim.
export function isManager(baseRole: string | null | undefined): boolean {
  return baseRole === "manager" || baseRole === "director";
}

export function isDirector(baseRole: string | null | undefined): boolean {
  return baseRole === "director";
}
